import React, { Component } from 'react';
import { Provider } from 'react-redux';
import {
  BrowserRouter, Navigate, Route, Routes,
} from 'react-router-dom';
import camelCaseKeysDeep from 'camelcase-keys-deep';
import _ from 'lodash';
import { getInitialState } from '../api';
import CheckinModal from '../containers/CheckinModal';
import ClientDashboard from '../containers/ClientDashboard';
import DateManagement from '../containers/DateManagement';
import DeskBooking from '../containers/DeskBooking';
import EmailSubscriptions from '../containers/EmailSubscriptions';
import Incidents from '../containers/Incidents';
import Notifications from '../containers/Notifications';
import Profile from '../containers/Profile';
import Projects from '../containers/Projects';
import Reports from '../containers/Reports';
import Timesheets from '../containers/Timesheets';
import Users from '../containers/Users';
import WorkSessions from '../containers/WorkSessions';
import YearCalendar from '../containers/YearCalendar';
import {
  arrangeById,
  getCSRFToken,
  getNormalizedData,
  getUsersProjects,
  isUserAdmin,
  isUserClient,
  isUserMember,
  isUserOwner,
} from '../helpers';
import Error from './elements/Error';
import Loader from './elements/Loader';
import StaticLoader from './elements/StaticLoader';
import { getNavToday } from '../helpers/dates';
import configureStore from '../redux/configureStore';
import Categories from './views/Categories';

type Props = {
  disableLogger: boolean,
};
type State = {
  initialStoreState: any | null,
};

class App extends Component<Props, State> {
  state: State = {
    initialStoreState: null,
  };

  componentDidMount() {
    const { pathname } = window.location;
    let year = 0;
    let weekNum = '';
    if (_.includes(pathname, 'week')) {
      const params = _.words(pathname);
      weekNum = _.last(params);
      year = _.first(params);
    } else {
      const today = getNavToday();
      weekNum = _.toString(today.week);
      year = today.year;
    }
    const { initialStoreState } = this.state;
    if (_.isNull(initialStoreState)) {
      getInitialState(year, weekNum)
        .then(({ data }) => {
          this.setState({
            initialStoreState: {
              dates: data.dates,
              projects: data.projects,
              roles: data.roles,
              users: camelCaseKeysDeep(data.user_list),
              daysInWeek: data.days_in_week,
              currentUser: {
                ...camelCaseKeysDeep(data.current_user),
                projects: getUsersProjects(camelCaseKeysDeep(data.current_user), data.projects),
              },
              projectsDropdown: data.projects_dropdown,
              categories: data.categories,
              projectStatistics: data.projects_stats,
              notifications: [],
              notificationsCount: 0,
              projectsWeeklyHours: data.projects_weekly_hours,
              holidays: data.holidays,
              vacations: camelCaseKeysDeep(arrangeById(data.vacations)),
              overtime: data.overtime,
              subscriptions: camelCaseKeysDeep(data.subscriptions),
              unresolvedIncidentsCount: data.unresolved_incidents_count || 0,
            },
          });
        });
    }
    this.resize(document.querySelector('.view-wrapper'));
    window.addEventListener('resize', this.handleResize);
  }

  handleResize: (() => void) = () => {
    const wrapper = document.querySelector('.view-wrapper');
    this.resize(wrapper);
  };

  resize(el: any) {
    if (el) {
      const adjustedWindowHeight = window.innerHeight * 0.01;
      el.style.setProperty('--vh', `${adjustedWindowHeight}px`);
    }
  }

  render(): React.ReactNode {
    const { initialStoreState } = this.state;
    const { disableLogger } = this.props;
    if (!initialStoreState) {
      return <StaticLoader display />;
    }

    const store = configureStore(getNormalizedData(initialStoreState as any, getCSRFToken(), window.location.pathname) as any, disableLogger);
    const isAdmin = isUserAdmin(initialStoreState.currentUser);
    const isOwner = isUserOwner(initialStoreState.currentUser);
    const isClient = isUserClient(initialStoreState.currentUser);
    const isMember = isUserMember(initialStoreState.currentUser);

    const RTimesheets: any = Timesheets as any;
    const RProjects: any = Projects as any;
    const RReports: any = Reports as any;
    const RUsers: any = Users as any;
    const RWorkSessions: any = WorkSessions as any;
    const RDateManagement: any = DateManagement as any;
    const RClientDashboard: any = ClientDashboard as any;
    const RNotifications: any = Notifications as any;
    const RProfile: any = Profile as any;
    const RYearCalendar: any = YearCalendar as any;
    const REmailSubscriptions: any = EmailSubscriptions as any;
    const RIncidents: any = Incidents as any;
    const RDeskBooking: any = DeskBooking as any;

    const ClientApp = (
      <Routes>
        <Route path="/profile" element={<RProfile />} />
        <Route path="/client" element={<RClientDashboard />} />
        <Route path="*" element={<Navigate to="/client" />} />
      </Routes>
    );

    const GeneralApp = (
      <Routes>
        {isAdmin || isOwner || isMember ? <Route path="/projects/:id" element={<RClientDashboard />} /> : null}
        {isAdmin || isOwner ? <Route path="/projects" element={<RProjects />} /> : null}
        {isAdmin || isOwner ? <Route path="/admin/users" element={<RUsers />} /> : null}
        {isAdmin || isOwner ? <Route path="/admin/work_sessions/users" element={<RWorkSessions />} /> : null}
        {isAdmin || isOwner ? <Route path="/notifications" element={<RNotifications />} /> : null}
        {isAdmin || isOwner ? <Route path="/date-management" element={<RDateManagement />} /> : null}
        {isAdmin || isOwner ? <Route path="/reports" element={<RReports />} /> : null}
        {isAdmin || isOwner ? <Route path="/email-subscriptions" element={<REmailSubscriptions />} /> : null}
        {isAdmin || isOwner ? <Route path="/incidents" element={<RIncidents />} /> : null}
        <Route path="/:year/week/:weekNum" element={<RTimesheets />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/desks" element={<RDeskBooking />} />
        <Route path="/year-calendar/:date" element={<RYearCalendar />} />
        <Route path="/profile" element={<RProfile />} />
        <Route path="/year-calendar" element={<RYearCalendar />} />
        <Route path="/" element={<RTimesheets />} />
      </Routes>
    );

    return (
      <>
        { /* Keep connected components inside Provider to ensure store context */}
        {(Provider as any) && (
          <Provider store={store as any}>
            <Loader />
            <Error />
            <BrowserRouter>
              <CheckinModal />
              {isClient ? ClientApp : GeneralApp}
            </BrowserRouter>
          </Provider>
        )}
      </>
    );
  }
}

export default App;
